import type {
  FinanceBillInput,
  FinanceCategoryInput,
  FinanceEntryInput,
  FinanceEntryStatusValue,
  FinanceEntryTypeValue,
  FinanceRecurrenceValue,
  PayFinanceEntryInput,
} from '@crm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { usePlatformPrefix } from '@/lib/platform-scope';
import type { PaginationMeta } from '@/modules/auth/types';

export const FINANCE_ROUTES = {
  BILLS: '/financeiro/contas',
  PAYABLES: '/financeiro/contas-a-pagar',
  RECEIVABLES: '/financeiro/contas-a-receber',
  CATEGORIES: '/financeiro/categorias',
} as const;

export interface FinanceCategory {
  id: string;
  companyId: string;
  name: string;
  type: FinanceEntryTypeValue;
  isActive: boolean;
  entriesCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface FinanceEntry {
  id: string;
  companyId: string;
  unitId: string | null;
  unitName: string | null;
  categoryId: string | null;
  categoryName: string | null;
  type: FinanceEntryTypeValue;
  status: FinanceEntryStatusValue;
  description: string;
  amount: number;
  dueDate: string;
  paidAt: string | null;
  paidAmount: number | null;
  notes: string | null;
  isOverdue: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Total {
  count: number;
  total: number;
}

export interface FinanceSummary {
  open: Total;
  paid: Total;
  overdue: Total;
}

export interface FinanceBill {
  id: string;
  companyId: string;
  unitId: string | null;
  unitName: string | null;
  categoryId: string | null;
  categoryName: string | null;
  type: FinanceEntryTypeValue;
  recurrence: FinanceRecurrenceValue;
  description: string;
  amount: number;
  startDate: string;
  endDate: string | null;
  notes: string | null;
  isActive: boolean;
  entriesCount: number;
  nextDueDate: string | null;
  createdAt: string;
  updatedAt: string;
}

interface SideTotal {
  payable: number;
  receivable: number;
}

export interface FinanceForecast {
  year: number;
  months: { month: number; payable: number; receivable: number }[];
  currentMonth: SideTotal;
  yearTotal: SideTotal;
}

export interface BillsQuery {
  page: number;
  perPage?: number;
  search?: string;
  type?: FinanceEntryTypeValue;
  status?: 'all' | 'active' | 'inactive';
  categoryId?: string;
  unitId?: string;
}

export type EntryStatusFilter = 'all' | 'open' | 'paid' | 'cancelled' | 'overdue';

export interface EntriesQuery {
  page: number;
  perPage?: number;
  search?: string;
  type?: FinanceEntryTypeValue;
  status?: EntryStatusFilter;
  categoryId?: string;
  unitId?: string;
  from?: string;
  to?: string;
}

/**
 * As chamadas de API do modulo, presas a `base` por closure. `/api/v1/finance`
 * no salao; `/api/v1/saas/financeiro/finance` reusada pelo saas admin - a
 * mesma tela, apontada para a empresa da propria plataforma.
 */
function createFinanceApi(base: string) {
  const CATEGORIES = `${base}/categories`;
  const ENTRIES = `${base}/entries`;
  const BILLS = `${base}/bills`;
  const FORECAST = `${base}/forecast`;

  return {
    listCategories(params: { type?: FinanceEntryTypeValue; onlyActive?: boolean } = {}) {
      const query = new URLSearchParams();
      if (params.type) query.set('type', params.type);
      if (params.onlyActive) query.set('onlyActive', 'true');

      return apiClient.get<{ data: FinanceCategory[] }>(`${CATEGORIES}?${query.toString()}`);
    },

    createCategory: (input: FinanceCategoryInput) =>
      apiClient.post<FinanceCategory>(CATEGORIES, input),
    updateCategory: (id: string, input: Partial<FinanceCategoryInput>) =>
      apiClient.patch<FinanceCategory>(`${CATEGORIES}/${id}`, input),
    removeCategory: (id: string) => apiClient.delete<{ message: string }>(`${CATEGORIES}/${id}`),

    listEntries(params: EntriesQuery) {
      const query = new URLSearchParams({ page: String(params.page) });
      if (params.perPage) query.set('perPage', String(params.perPage));
      if (params.search) query.set('search', params.search);
      if (params.type) query.set('type', params.type);
      if (params.status && params.status !== 'all') query.set('status', params.status);
      if (params.categoryId) query.set('categoryId', params.categoryId);
      if (params.unitId) query.set('unitId', params.unitId);
      if (params.from) query.set('from', params.from);
      if (params.to) query.set('to', params.to);

      return apiClient.get<{ data: FinanceEntry[]; meta: PaginationMeta; summary: FinanceSummary }>(
        `${ENTRIES}?${query.toString()}`,
      );
    },

    listBills(params: BillsQuery) {
      const query = new URLSearchParams({ page: String(params.page) });
      if (params.perPage) query.set('perPage', String(params.perPage));
      if (params.search) query.set('search', params.search);
      if (params.type) query.set('type', params.type);
      if (params.status && params.status !== 'all') query.set('status', params.status);
      if (params.categoryId) query.set('categoryId', params.categoryId);
      if (params.unitId) query.set('unitId', params.unitId);

      return apiClient.get<{ data: FinanceBill[]; meta: PaginationMeta }>(
        `${BILLS}?${query.toString()}`,
      );
    },

    createBill: (input: FinanceBillInput) => apiClient.post<FinanceBill>(BILLS, input),
    updateBill: (id: string, input: Partial<FinanceBillInput>) =>
      apiClient.patch<FinanceBill>(`${BILLS}/${id}`, input),
    removeBill: (id: string) => apiClient.delete<{ message: string }>(`${BILLS}/${id}`),

    forecast(params: { year?: number; type?: FinanceEntryTypeValue; unitId?: string } = {}) {
      const query = new URLSearchParams();
      if (params.year) query.set('year', String(params.year));
      if (params.type) query.set('type', params.type);
      if (params.unitId) query.set('unitId', params.unitId);

      return apiClient.get<FinanceForecast>(`${FORECAST}?${query.toString()}`);
    },

    createEntry: (input: FinanceEntryInput) => apiClient.post<FinanceEntry>(ENTRIES, input),
    updateEntry: (id: string, input: Partial<FinanceEntryInput>) =>
      apiClient.patch<FinanceEntry>(`${ENTRIES}/${id}`, input),
    payEntry: (id: string, input: PayFinanceEntryInput) =>
      apiClient.post<FinanceEntry>(`${ENTRIES}/${id}/pay`, input),
    reopenEntry: (id: string) => apiClient.post<FinanceEntry>(`${ENTRIES}/${id}/reopen`, {}),
    cancelEntry: (id: string) => apiClient.post<FinanceEntry>(`${ENTRIES}/${id}/cancel`, {}),
    removeEntry: (id: string) => apiClient.delete<{ message: string }>(`${ENTRIES}/${id}`),
  };
}

/** A instancia usada pelo salao - a maioria do sistema so precisa desta. */
export const financeApi = createFinanceApi('/api/v1/finance');

/** A mesma API, apontada para a empresa certa conforme quem esta usando a tela. */
function useFinanceApi() {
  return createFinanceApi(`/api/v1${usePlatformPrefix()}/finance`);
}

function keysFor(prefix: string) {
  return {
    all: ['finance', prefix] as const,
    categories: ['finance', prefix, 'categories'] as const,
    entries: ['finance', prefix, 'entries'] as const,
    bills: ['finance', prefix, 'bills'] as const,
    forecast: ['finance', prefix, 'forecast'] as const,
  };
}

export function useFinanceBills(params: BillsQuery) {
  const api = useFinanceApi();
  const keys = keysFor(usePlatformPrefix());

  return useQuery({
    queryKey: [...keys.bills, params],
    queryFn: () => api.listBills(params),
    placeholderData: (previous) => previous,
  });
}

export function useFinanceForecast(
  params: { year?: number; type?: FinanceEntryTypeValue; unitId?: string } = {},
) {
  const api = useFinanceApi();
  const keys = keysFor(usePlatformPrefix());

  return useQuery({
    queryKey: [...keys.forecast, params],
    queryFn: () => api.forecast(params),
    placeholderData: (previous) => previous,
  });
}

export function useFinanceCategories(
  params: { type?: FinanceEntryTypeValue; onlyActive?: boolean } = {},
) {
  const api = useFinanceApi();
  const keys = keysFor(usePlatformPrefix());

  return useQuery({
    queryKey: [...keys.categories, params],
    queryFn: () => api.listCategories(params),
    placeholderData: (previous) => previous,
  });
}

export function useFinanceEntries(params: EntriesQuery) {
  const api = useFinanceApi();
  const keys = keysFor(usePlatformPrefix());

  return useQuery({
    queryKey: [...keys.entries, params],
    queryFn: () => api.listEntries(params),
    placeholderData: (previous) => previous,
  });
}

/** Categoria e lancamento se referenciam: uma escrita invalida os dois. */
function useFinanceMutation<TArgs>(mutationFn: (args: TArgs) => Promise<unknown>) {
  const queryClient = useQueryClient();
  const keys = keysFor(usePlatformPrefix());

  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.all }),
  });
}

export function useCreateFinanceCategory() {
  const api = useFinanceApi();
  return useFinanceMutation((input: FinanceCategoryInput) => api.createCategory(input));
}

export function useUpdateFinanceCategory() {
  const api = useFinanceApi();
  return useFinanceMutation(({ id, input }: { id: string; input: Partial<FinanceCategoryInput> }) =>
    api.updateCategory(id, input),
  );
}

export function useDeleteFinanceCategory() {
  const api = useFinanceApi();
  return useFinanceMutation((id: string) => api.removeCategory(id));
}

export function useCreateFinanceBill() {
  const api = useFinanceApi();
  return useFinanceMutation((input: FinanceBillInput) => api.createBill(input));
}

export function useUpdateFinanceBill() {
  const api = useFinanceApi();
  return useFinanceMutation(({ id, input }: { id: string; input: Partial<FinanceBillInput> }) =>
    api.updateBill(id, input),
  );
}

export function useDeleteFinanceBill() {
  const api = useFinanceApi();
  return useFinanceMutation((id: string) => api.removeBill(id));
}

export function useCreateFinanceEntry() {
  const api = useFinanceApi();
  return useFinanceMutation((input: FinanceEntryInput) => api.createEntry(input));
}

export function useUpdateFinanceEntry() {
  const api = useFinanceApi();
  return useFinanceMutation(({ id, input }: { id: string; input: Partial<FinanceEntryInput> }) =>
    api.updateEntry(id, input),
  );
}

export function usePayFinanceEntry() {
  const api = useFinanceApi();
  return useFinanceMutation(({ id, input }: { id: string; input: PayFinanceEntryInput }) =>
    api.payEntry(id, input),
  );
}

export function useReopenFinanceEntry() {
  const api = useFinanceApi();
  return useFinanceMutation((id: string) => api.reopenEntry(id));
}

export function useCancelFinanceEntry() {
  const api = useFinanceApi();
  return useFinanceMutation((id: string) => api.cancelEntry(id));
}

export function useDeleteFinanceEntry() {
  const api = useFinanceApi();
  return useFinanceMutation((id: string) => api.removeEntry(id));
}
